import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingDown, Users, ReceiptText, GraduationCap, ShieldAlert } from "lucide-react";
import AriOrb from "@/components/ari/AriOrb";
import { PanelAmbience, HC_STYLES } from "@/components/marketing/HeroCinematic";
import { useTilt, Glare } from "@/components/marketing/Depth4D";

const INSIGHTS = [
  { icon: TrendingDown, domain: "Cost Control", confidence: 94, tone: "warning",
    title: "Produce spend is trending up",
    body: "Your greengrocer's average invoice rose 11% over 3 weeks while covers stayed flat — worth a supplier review." },
  { icon: Users, domain: "Workforce", confidence: 91, tone: "gold",
    title: "Friday is over-staffed vs demand",
    body: "Labour is running ~8% above the sweet spot on Fridays 3–6pm. Shifting one server later could protect margin without hurting service." },
  { icon: ReceiptText, domain: "Receipts & Cash", confidence: 97, tone: "gold",
    title: "Possible duplicate invoice detected",
    body: "Two invoices from the same supplier match on date and total — flagged before it was paid twice." },
  { icon: GraduationCap, domain: "Training", confidence: 88, tone: "gold",
    title: "Two new starters are behind",
    body: "Wine-service modules are lagging for your latest hires — a targeted refresher would lift table confidence this week." },
  { icon: ShieldAlert, domain: "Compliance", confidence: 96, tone: "success",
    title: "All fridge temperature checks logged",
    body: "Every opening check is complete for 14 days running — you're audit-ready with a full digital trail." },
];

const TONE = { gold: "#E9CE7A", warning: "#E0A150", success: "#AEBE7F" };
const EASE = [0.16, 1, 0.3, 1];

// Same pristine glass + living gold network as the hero conversation panel.
export const OperationsBrainShowcase = () => {
  const [active, setActive] = useState(0);
  const tilt = useTilt(4);
  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % INSIGHTS.length), 3800);
    return () => clearInterval(t);
  }, []);
  const cur = INSIGHTS[active];
  const Icon = cur.icon;
  const c = TONE[cur.tone];

  return (
    <motion.div className="group relative overflow-hidden" data-testid="ops-brain-showcase" {...tilt.handlers}
      style={{ ...tilt.style, border: "1px solid rgba(212,175,55,0.18)", boxShadow: "0 40px 80px -40px rgba(0,0,0,0.9)" }}>
      <style>{HC_STYLES}</style>
      <PanelAmbience />
      <div className="absolute inset-0 z-[1] pointer-events-none" style={{ background: "linear-gradient(160deg, rgba(12,12,15,0.55) 0%, rgba(10,10,12,0.45) 55%, rgba(9,9,12,0.6) 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)" }} />
      <Glare glare={tilt.glare} />
      <div className="relative z-[2] p-6 sm:p-8">
        <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-white/[0.07]">
          <div className="flex items-center gap-2.5">
            <AriOrb size={26} state="insight" />
            <p className="hc-crisp text-[11px] font-mono-lux uppercase tracking-[0.25em] text-white/60">ARI · Living Intelligence</p>
          </div>
          <span className="hc-crisp inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/45">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#5FD3B0", boxShadow: "0 0 6px rgba(95,211,176,0.7)" }} /> Live
          </span>
        </div>

        <div className="min-h-[180px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} style={{ transformPerspective: 800 }}
              initial={{ opacity: 0, y: 12, rotateX: 14 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.6, ease: EASE }}>
              <div className="relative overflow-hidden rounded-2xl rounded-tl-[7px] p-5"
                style={{ background: "linear-gradient(180deg,rgba(212,175,55,0.15),rgba(212,175,55,0.045))", border: "1px solid rgba(212,175,55,0.28)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16), 0 14px 30px -16px rgba(0,0,0,0.8)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ background: "radial-gradient(circle at 35% 28%, rgba(251,239,196,0.26), rgba(212,175,55,0.08) 60%, rgba(20,16,10,0.9))", boxShadow: `inset 0 0 0 1px ${c}66, inset 0 -3px 6px rgba(0,0,0,0.45)` }}>
                    <Icon className="w-5 h-5" style={{ color: c }} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1.5">
                      <span className="hc-crisp text-[10px] font-mono-lux uppercase tracking-widest text-white/50">{cur.domain}</span>
                      <span className="hc-crisp text-[10px] font-mono-lux" style={{ color: c }}>{cur.confidence}% CONFIDENCE</span>
                    </div>
                    <h4 className="hc-crisp hc-title hc-title--bright font-serif-lux text-2xl leading-tight">{cur.title}</h4>
                    <p className="hc-crisp text-sm text-white/70 mt-2 leading-relaxed">{cur.body}</p>
                  </div>
                </div>
                <div className="mt-4 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
                  <motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${c}, #F6E3A6)` }}
                    initial={{ width: 0 }} animate={{ width: `${cur.confidence}%` }} transition={{ duration: 1.2, ease: EASE, delay: 0.2 }} />
                </div>
                <span aria-hidden className="hc-sheen" />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-1.5 mt-6" data-testid="ops-brain-dots">
          {INSIGHTS.map((_, i) => (
            <button key={i} onClick={() => setActive(i)} aria-label={`Insight ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${i === active ? "w-8 bg-[#D4AF37]" : "w-2 bg-white/15 hover:bg-white/30"}`} />
          ))}
        </div>
      </div>
    </motion.div>
  );
};
